<template>
  <div>
    <div class="float-right">
      <b-button variant="default" @click="cancel">Cancel</b-button>
      <b-btn variant="info" @click.prevent="save">
        <template v-if="!saveLoading">Save</template>
        <template v-else>
          <b-spinner small />
          <span>Saving...</span>
        </template>
      </b-btn>
    </div>

    <h2>
      {{
        question && question.id ? `Edit Q${question.id}` : `Create New Question`
      }}
    </h2>

    <b-row class="my-4">
      <b-col class="text-left question-text" md="6">
        <b-row class="my-3">
          <b-col sm="3">
            <label>Required</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.question.isRequired.$model"
              :value="1"
              :unchecked-value="0"
            />
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Question Type</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-select
                v-model="$v.question.type.$model"
                :state="validateState('type')"
                :options="questionTypeOptions"
                @change="onQuestionTypeChanged"
              >
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Label</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-input
                v-model="$v.question.label.$model"
                type="text"
                :state="validateState('label')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Preamble</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.question.preamble.$model"
              type="text"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Prompt</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea v-model="$v.question.prompt.$model" type="text" />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Explanation</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.question.explanation.$model"
              rows="2"
            />
          </b-col>
        </b-row>

        <b-row class="my-3" v-show="question.type === 'SLIDER'">
          <b-col sm="3">
            <label>Calculate Totals</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.question.calculateTotals.$model"
              :value="1"
              :unchecked-value="0"
            />
            <b-alert
              show
              variant="warning"
              v-if="question.calculateTotals === 1"
            >
              If Variables have the '%' suffix, this will mark the response
              invalid if variable values do not total 100%.
            </b-alert>
          </b-col>
        </b-row>
        <b-row class="my-3" v-if="isQuestionTypeSlider">
          <b-col sm="3">
            <label>Enable Unknown Option</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.question.is_unknown_enabled.$model"
            />
          </b-col>
        </b-row>
        <b-row class="my-3" v-if="question.is_unknown_enabled">
          <b-col sm="3">
            <label>Unknown Option Label</label>
          </b-col>
          <b-col sm="9">
            <b-form-group>
              <b-form-input
                v-model="$v.question.unknown_option_label.$model"
                type="text"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="my-3" v-if="question.type === 'SELECT_ANY'">
          <b-col sm="3">
            <label>Enable Select All</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.question.is_select_all_enabled.$model"
            />
          </b-col>
        </b-row>

        <template v-if="hasChart">
          <b-row class="my-3">
            <b-col sm="3">
              <label>Chart Type</label>
            </b-col>
            <b-col sm="9">
              <b-form-group>
                <b-form-select
                  v-model="$v.question.chart.chart_type.$model"
                  :options="chartTypeOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <chart-config
            :chart_type="chartType"
            :config="chartConfig"
            @configChanged="onConfigChanged"
          />
        </template>

        <template v-if="showMaximumSelectionsSettings">
          <b-row class="my-3">
            <b-col sm="3">
              <label>Maximum Selections</label>
            </b-col>
            <b-col sm="9">
              <b-form-group
                :invalid-feedback="maximumSelectionsInvalidFeedback"
                description="If the Maximum Selections is 0, then the user can choose any number of options for the answer."
              >
                <span id="maximum_selections">
                  <b-form-input
                    v-model="$v.question.maximum_selections.$model"
                    type="number"
                    :state="validateState('maximum_selections')"
                    min="0"
                    :max="notDeletedVariables.length"
                    :disabled="!!question.is_select_all_enabled"
                  />
                </span>
              </b-form-group>
              <b-tooltip
                v-if="!!question.is_select_all_enabled"
                :target="`maximum_selections`"
                :delay="{ show: 500, hide: 100 }"
                triggers="click hover"
              >
                This option cannot be selected when `Enable Select All` option
                is already selected.
              </b-tooltip>
            </b-col>
          </b-row>
        </template>
        <b-row class="my-3" v-if="isRandomizeVariablesAvailable">
          <b-col sm="3">
            <label>Randomize variables</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.question.randomize_variables.$model"
            />
          </b-col>
        </b-row>

        <v-wait :for="jobQuestionLoaderId">
          <template slot="waiting">
            <HalfCircleSpinner />
            <div class="spinner-message my-3 text-center">
              Loading the job question data...
            </div>
          </template>
          <b-row class="my-3">
            <b-col sm="3">
              <label>Job Question</label>
            </b-col>
            <b-col sm="9">
              <b-form-group>
                <b-form-select
                  v-model="$v.question.job_question_id.$model"
                  :options="jobQuestionOptions"
                >
                  <template slot="first">
                    <option :value="null">
                      -- Select --
                    </option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </v-wait>
      </b-col>

      <!-- ---------------- variables -------------------- -->

      <b-col md="6">
        <QuestionEditVariables
          :question="$v.question"
          :exclusive-id="exclusiveId"
          @update-exclusive="setExclusive"
          @add-variable="addVariable"
          @delete-variable="deleteVariable"
          @order-variables="orderVariables"
          @add-variable-option="addVariableOption"
          @remove-option="onRemoveOption"
          @cancel-remove-option="onCancelRemoveOption"
          @set-visibility-condition="onSetVariableVisibilityCondition"
          @set-response-validation-condition="onSetResponseVariableCondition"
        />
      </b-col>
    </b-row>

    <!-- ------------- conditions -------------- -->

    <b-row>
      <b-col sm="12">
        <visibility-condition-form
          :condition="visibilityCondition"
          @set-condition="onSetVisibilityCondition"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="question.type === 'SLIDER' && question.calculateTotals"
      class="mt-3"
    >
      <b-col sm="12">
        <value-condition-form
          :condition="responseValidationCondition"
          @set-condition="onSetResponseValidationCondition"
        />
      </b-col>
    </b-row>

    <!-- ------------- preview -------------- -->

    <div>
      <h5 class="text-left">Question Preview</h5>
      <QuestionForm
        :survey="{}"
        :question="question"
        :variables="[]"
        :preview="true"
      />
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required,
  integer,
  minValue,
  requiredIf,
} from 'vuelidate/lib/validators';
import Api from '@/providers/Api.js';
import QuestionForm from '@/components/QuestionForm.vue';
import VisibilityConditionForm from '@/components/question/condition/VisibilityConditionForm';
import ValueConditionForm from '@/components/question/condition/ValueConditionForm';
import QuestionEditVariables from '@/components/question/QuestionEditVariables';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import ChartConfig from '@/components/question/ChartConfig';
import { HalfCircleSpinner } from 'epic-spinners';
import {
  CONDITION_VARIANTS,
  QUESTION_DEFAULT_CHART_TYPE,
  QUESTION_TYPES,
} from '@/store';
import maxBy from 'lodash/maxBy';
import { createNewVariableOption } from '@/helpers/VariableOptionHelper';
export default {
  name: 'QuestionEditForm',
  mixins: [validationMixin],
  components: {
    QuestionForm,
    VisibilityConditionForm,
    QuestionEditVariables,
    ChartConfig,
    HalfCircleSpinner,
    ValueConditionForm,
  },
  props: {
    initialQuestion: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  data: function () {
    const question = { ...this.initialQuestion };
    const getExclusiveId = () => {
      if (this.initialQuestion.type !== QUESTION_TYPES.SELECT_ANY) return null;
      const exclusiveVariable = this.initialQuestion.variables.find(
        (v) => v.is_exclusive
      );
      return exclusiveVariable ? exclusiveVariable.id : null;
    };
    return {
      saveLoading: false,
      question: {
        id: this.initialQuestion ? question.id : null,
        isRequired: this.initialQuestion ? question.required : 0,
        type: this.initialQuestion ? question.type : '',
        label: this.initialQuestion ? question.label : '',
        preamble: this.initialQuestion ? question.preamble : '',
        prompt: this.initialQuestion ? question.prompt : '',
        explanation: this.initialQuestion ? question.explanation : '',
        calculateTotals: this.initialQuestion ? question.calculate_totals : 0,
        variables: this.initialQuestion ? question.variables : [],
        chart: this.initialQuestion.chart ? question.chart : {},
        condition: this.initialQuestion.condition ? question.condition : [],
        maximum_selections: this.initialQuestion
          ? question.maximum_selections
          : 0,
        randomize_variables: this.initialQuestion
          ? this.initialQuestion.randomize_variables
          : 0,
        job_question_id: this.initialQuestion.job_question_id
          ? question.job_question_id
          : null,
        is_unknown_enabled: this.initialQuestion.is_unknown_enabled
          ? this.initialQuestion.is_unknown_enabled
          : 0,
        unknown_option_label: this.initialQuestion.unknown_option_label
          ? this.initialQuestion.unknown_option_label
          : 'I don’t know the value',
        is_select_all_enabled: this.initialQuestion
          ? question.is_select_all_enabled
          : 0,
      },
      exclusiveId: getExclusiveId(),
      jobQuestions: [],
    };
  },
  validations: function () {
    let variableOptions = {};
    if (this.question.type === QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE) {
      variableOptions = {
        required,
        minLength: (options) => {
          if (!options) return false;
          const notDeletedOptions = options.filter((o) => !o.delete_me);
          return notDeletedOptions.length >= 1;
        },
        $each: {
          name: {
            required: requiredIf((model) => !model.delete_me),
          },
          value: {
            required: requiredIf((model) => !model.delete_me),
          },
        },
      };
    }
    return {
      question: {
        isRequired: {},
        preamble: {},
        prompt: {},
        explanation: {},
        calculateTotals: {},
        randomize_variables: {},
        is_select_all_enabled: {},
        variables: {
          $each: {
            id: {},
            uid: {},
            delete_me: {},
            visible: {},
            order: {},
            input_label: {},
            dv_name: {},
            prefix: {},
            suffix: {},
            singular_suffix: {},
            short_label: {},
            max: {},
            min: {},
            max_label: {},
            min_label: {},
            step: {},
            is_exclusive: {},
            set_zero_label: {},
            is_set_zero_hidden: {},
            variableOptions,
          },
        },
        chart: {
          chart_type: {},
        },
        type: {
          required,
        },
        label: {
          required,
        },
        maximum_selections: {
          integer,
          minValue: minValue(0),
          lessOrEqualThanVariables: (value, question) => {
            if (question.type !== QUESTION_TYPES.SELECT_ANY) return true;
            const notDeletedVariables = question.variables.filter(
              (v) => !v.delete_me
            );
            return parseInt(value) <= notDeletedVariables.length;
          },
        },
        job_question_id: {},
        is_unknown_enabled: {},
        unknown_option_label: {},
      },
    };
  },
  created: async function () {
    if (!this.question.chart.chart_type) {
      this.$set(
        this.question.chart,
        'chart_type',
        QUESTION_DEFAULT_CHART_TYPE[this.question.type]
      );
    }
    await this.getJobQuestions();
  },
  computed: {
    jobQuestionLoaderId: function () {
      return `loading_${this.$options.name}`;
    },
    questionTypeOptions: function () {
      return this.$store.state.questionTypeOptions;
    },
    invalidFeedback: function () {
      return 'This field is required';
    },
    maximumSelectionsInvalidFeedback: function () {
      if (!this.$v.question.maximum_selections.integer) {
        return 'Maximum Selections should be a number.';
      }
      if (!this.$v.question.maximum_selections.minValue) {
        return "Maximum Selections can't be less than 0.";
      }
      if (!this.$v.question.maximum_selections.lessOrEqualThanVariables) {
        return "Maximum Selections can't be more than the number of Variables.";
      }
      return null;
    },
    chartType: function () {
      return this.question.chart.chart_type;
    },
    hasChart: function () {
      return (
        this.question.type !== QUESTION_TYPES.FIND_SCHOOL &&
        this.question.type !== QUESTION_TYPES.CONFIRMATION
      );
    },
    chartConfig: function () {
      return this.question.chart && this.question.chart.chart_config
        ? this.question.chart.chart_config
        : {};
    },
    chartTypeOptions: function () {
      return this.$store.state.chartTypeOptions.filter((o) => {
        return o.questionTypes.includes(this.question.type);
      });
    },
    showMaximumSelectionsSettings: function () {
      return this.question.type === QUESTION_TYPES.SELECT_ANY;
    },
    notDeletedVariables: function () {
      return this.question.variables.filter((v) => !v.delete_me);
    },
    isRandomizeVariablesAvailable: function () {
      return [
        QUESTION_TYPES.SELECT_ANY,
        QUESTION_TYPES.SELECT_ONE,
        QUESTION_TYPES.SLIDER,
      ].includes(this.question.type);
    },
    jobQuestionOptions: function () {
      return this.jobQuestions.map((jQ) => {
        return {
          text: jQ.label,
          value: jQ.id,
        };
      });
    },
    isQuestionTypeSlider: function () {
      return this.question.type === QUESTION_TYPES.SLIDER;
    },
    visibilityCondition: function () {
      return this.question && Array.isArray(this.question.condition)
        ? this.question.condition.find(
            (cond) => cond.variant === CONDITION_VARIANTS.VISIBILITY
          )
        : null;
    },
    responseValidationCondition: function () {
      return this.question && Array.isArray(this.question.condition)
        ? this.question.condition.find(
            (cond) => cond.variant === CONDITION_VARIANTS.RESPONSE_VALIDATION
          )
        : null;
    },
  },
  methods: {
    getJobQuestions: async function () {
      try {
        this.$wait.start(this.jobQuestionLoaderId);
        this.jobQuestions = await Api.getJobQuestionsForQuestion();
      } catch (error) {
        process.env.NODE_ENV !== 'production' && console.error(error);
        this.$toasted.show('Some error occurs', {
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.jobQuestionLoaderId);
      }
    },
    save: async function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        if (this.$v.question.variables.$anyError) {
          const collection = this.$v.question.variables.$each.$iter;
          for (let key in collection) {
            if (collection.hasOwnProperty(key) && collection[key].$anyError) {
              collection[key].$model.visible = true;
            }
          }
        }
        return;
      }

      this.saveLoading = true;

      const questionData = {
        ...this.initialQuestion,
        id: this.question.id,
        required: this.question.isRequired,
        type: this.question.type,
        label: this.question.label,
        preamble: this.question.preamble,
        prompt: this.question.prompt,
        explanation: this.question.explanation,
        calculate_totals: this.question.calculateTotals,
        variables: this.getVariables(),
        chart: this.question.chart,
        maximum_selections: this.question.maximum_selections,
        job_question_id: this.question.job_question_id,
        condition: this.question.condition,
        is_unknown_enabled: this.question.is_unknown_enabled,
        unknown_option_label: this.question.unknown_option_label,
        is_select_all_enabled: this.question.is_select_all_enabled,
      };
      if (this.isRandomizeVariablesAvailable) {
        questionData.randomize_variables = this.question.randomize_variables;
      }

      try {
        const question = await Api.updateQuestion(questionData);
        this.syncQuestion(question);
        this.$toasted.success('Question saved', { duration: 3000 });
        this.$emit('questionSaved', question);
      } catch (error) {
        this.showErrors(error);
      } finally {
        this.saveLoading = false;
        this.$v.$reset();
      }
    },
    syncQuestion: function (question) {
      this.question.id = question.id;
      this.question.isRequired = question.required;
      this.question.type = question.type;
      this.question.label = question.label;
      this.question.preamble = question.preamble;
      this.question.prompt = question.prompt;
      this.question.explanation = question.explanation;
      this.question.calculateTotals = question.calculate_totals;
      this.question.variables = question.variables;
      this.question.chart = question.chart;
      this.question.maximum_selections = question.maximum_selections;
      this.question.randomize_variables = question.randomize_variables;
      this.question.job_question_id = question.job_question_id;
      this.question.condition = question.condition;
      this.question.is_unknown_enabled = question.is_unknown_enabled;
      this.question.unknown_option_label = question.unknown_option_label;
      this.question.is_select_all_enabled = question.is_select_all_enabled;
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v.question[name];
      return $dirty ? !$error : null;
    },
    showErrors: function (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    onConfigChanged: function (config) {
      if (!this.question.chart) {
        this.question.chart = {};
      }
      this.$set(this.question.chart, 'chart_config', config);
    },
    onQuestionTypeChanged: function () {
      if (!this.hasChart) {
        this.setEmptyConfig();
      }
      this.$set(
        this.question.chart,
        'chart_type',
        QUESTION_DEFAULT_CHART_TYPE[this.question.type]
      );
    },
    setEmptyConfig: function () {
      this.$set(this.question.chart, 'chart_config', {});
    },
    setExclusive: function (exclusiveId) {
      this.exclusiveId = exclusiveId;
      this.$v.question.variables.$touch();
    },
    getVariables: function () {
      const isExclusive = ({ id }) =>
        this.exclusiveId &&
        this.question.type === QUESTION_TYPES.SELECT_ANY &&
        id === this.exclusiveId;
      return this.question.variables.map((variable) => {
        return {
          ...variable,
          is_exclusive: isExclusive(variable) ? 1 : 0,
        };
      });
    },
    addVariable: function () {
      const maxV = maxBy(this.question.variables, (v) => v.order);
      const variableId = Date.now();
      const newVariable = {
        uid: variableId,
        id: null,
        order: maxV ? maxV.order + 1 : 1,
        input_label: '[New Variable]',
        short_label: '',
        max: null,
        min: null,
        max_label: null,
        min_label: null,
        step: null,
        visible: true,
        delete_me: false,
        is_exclusive: 0,
      };
      if (this.question.type === QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE) {
        newVariable.variableOptions = [createNewVariableOption(variableId)];
      }
      this.question.variables.push(newVariable);
      this.$v.question.variables.$touch();
    },
    deleteVariable: function (v) {
      this.$set(v, 'delete_me', !v.delete_me);
      this.orderVariables();
      this.$v.question.variables.$touch();
    },
    orderVariables: function () {
      const shownVars = this.question.variables.filter((v) => !v.delete_me);
      shownVars.forEach((v, i) => {
        this.$set(v, 'order', i + 1);
      });
    },
    addOrUpdateCondition: function (newCondition, conditionVariant) {
      const index = this.question.condition.findIndex(
        (cond) => cond.variant === conditionVariant
      );
      if (index === -1 && newCondition) {
        this.question.condition.push(newCondition);
      } else if (newCondition) {
        this.$set(this.question.condition, index, newCondition);
      } else {
        this.question.condition.splice(index, 1);
      }
    },

    onSetVisibilityCondition: function (newCondition) {
      this.addOrUpdateCondition(newCondition, CONDITION_VARIANTS.VISIBILITY);
    },
    onSetResponseValidationCondition: function (newCondition) {
      this.addOrUpdateCondition(
        newCondition,
        CONDITION_VARIANTS.RESPONSE_VALIDATION
      );
    },
    addVariableOption: function (varId, option) {
      const varIndex = this.question.variables.findIndex(({ id, uid }) =>
        id ? id === varId : uid === varId
      );
      if (varIndex === -1) return;
      if (Array.isArray(this.question.variables[varIndex].variableOptions)) {
        this.$set(
          this.question.variables[varIndex].variableOptions,
          this.question.variables[varIndex].variableOptions.length,
          option
        );
      } else {
        this.$set(this.question.variables[varIndex], 'variableOptions', [
          option,
        ]);
      }
    },
    onRemoveOption: function (optionId, variableId) {
      this.setOptionDeleteMe(optionId, variableId);
    },
    onCancelRemoveOption: function (optionId, variableId) {
      this.setOptionDeleteMe(optionId, variableId, false);
    },
    setOptionDeleteMe: function (optionId, variableId, flag = true) {
      const varIndex = this.question.variables.findIndex(({ id, uid }) =>
        id ? id === variableId : uid === variableId
      );
      if (varIndex === -1) return;
      const optionIndex = this.question.variables[
        varIndex
      ].variableOptions.findIndex(
        (vo) => vo.id === optionId || vo.uid === optionId
      );
      if (optionIndex === -1) return;
      this.$set(
        this.question.variables[varIndex].variableOptions[optionIndex],
        'delete_me',
        flag
      );
    },
    setVariableCondition: function (varId, condition, variant) {
      const varIndex = this.question.variables.findIndex(({ id, uid }) =>
        id ? id === varId : uid === varId
      );
      if (varIndex === -1) return;
      if (
        !this.question.variables[varIndex].condition ||
        !Array.isArray(this.question.variables[varIndex].condition)
      ) {
        if (condition) {
          this.$set(this.question.variables[varIndex], 'condition', [
            condition,
          ]);
        }
        return;
      }
      const index = this.question.variables[varIndex].condition.findIndex(
        (cond) => cond.variant === variant
      );
      if (index === -1 && condition) {
        this.question.variables[varIndex].condition.push(condition);
      } else if (condition) {
        this.$set(
          this.question.variables[varIndex].condition,
          index,
          condition
        );
      } else {
        this.question.variables[varIndex].condition.splice(index, 1);
      }
    },
    onSetVariableVisibilityCondition: function (varId, condition) {
      this.setVariableCondition(
        varId,
        condition,
        CONDITION_VARIANTS.VISIBILITY
      );
    },
    onSetResponseVariableCondition: function (varId, newCondition) {
      this.setVariableCondition(
        varId,
        newCondition,
        CONDITION_VARIANTS.RESPONSE_VALIDATION
      );
    },
    cancel: function () {
      this.$router.push('/admin/question');
    },
  },
  watch: {
    'question.chart.chart_type': function () {
      this.setEmptyConfig();
    },
    'question.type': function (newType) {
      this.question.randomize_variables = 0;
      this.exclusiveId = null;
      if (newType === QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE) {
        this.question.variables.forEach((variable) => {
          const variableId = variable.id || variable.uid;
          this.$set(variable, 'variableOptions', []);
          this.$set(
            variable.variableOptions,
            0,
            createNewVariableOption(variableId)
          );
        });
      } else {
        this.question.variables.forEach((variable) => {
          delete variable.variableOptions;
        });
      }
    },
    'question.is_select_all_enabled': {
      handler: function (newVal) {
        if (newVal) {
          this.$set(this.question, 'maximum_selections', 0);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped lang="scss">
.question-edit {
  display: inline;
}

.custom-controls-stacked {
  min-height: 85px;
  max-height: 170px;
  overflow-y: scroll;
}

textarea {
  min-height: 60px;
}

.modal-footer > div {
  width: 100%;
}
</style>
